import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, XCircle, Mail, ArrowLeft, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import API_BASE from '../../utils/apiBase';
import TenantWelcomeSplash from '../../components/portal/TenantWelcomeSplash';

const API = API_BASE;

const firstNameOf = (fullName) => String(fullName || '').trim().split(/\s+/)[0] || '';

export function sanitizeWorkerNextPath(value) {
  const path = String(value || '').trim();
  if (!path) return '/worker/dashboard';
  if (/^#[A-Za-z0-9_-]+$/.test(path)) return `/worker/dashboard${path}`;
  if (/^\/worker\/dashboard(?:#[A-Za-z0-9_-]+)?$/.test(path)) return path;
  if (/^\/worker\/dashboard\?step=[A-Za-z0-9_-]+$/.test(path)) return path;
  if (/^\/worker\/dashboard\?view=(?:shifts|compliance|file)$/.test(path)) return path;
  if (/^\/worker\/forms\/[A-Za-z0-9_-]+$/.test(path)) return path;
  return '/worker/dashboard';
}

export default function WorkerVerifyPage() {
  const [searchParams] = useSearchParams();
  const { shortCode = '' } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying | welcome | error
  const [error, setError] = useState('');
  const [employee, setEmployee] = useState(null);
  const [isApplicant, setIsApplicant] = useState(false);
  const [branding, setBranding] = useState(null);
  const workspaceSlugFromUrl = searchParams.get('workspace') || searchParams.get('workspace_slug') || '';
  const [nextWorkerPath, setNextWorkerPath] = useState(() => sanitizeWorkerNextPath(searchParams.get('next')));

  const workforceReturnPath = (slug) => `/workforce/${slug}`;
  const knownWorkspaceSlug = () => workspaceSlugFromUrl || localStorage.getItem('workerWorkspaceSlug') || '';

  // Best-effort tenant branding for the welcome splash. Never blocks or breaks
  // the login/redirect if it is unavailable.
  const loadBranding = useCallback((slug) => {
    if (!slug) return;
    try {
      const promise = axios.get(`${API}/public/workforce/${slug}`);
      if (promise && typeof promise.then === 'function') {
        promise.then((res) => setBranding(res?.data || null)).catch(() => {});
      }
    } catch (_e) {
      /* branding is decorative only */
    }
  }, []);

  const goToNextAction = useCallback(() => {
    navigate(nextWorkerPath);
  }, [navigate, nextWorkerPath]);

  const verifyAccess = useCallback(async () => {
    try {
      const token = searchParams.get('token');
      let response;
      if (shortCode) {
        response = await axios.post(`${API}/worker/short-access/${encodeURIComponent(shortCode)}`);
      } else if (token) {
        response = await axios.post(`${API}/worker/verify-login`, { token });
      } else {
        setStatus('error');
        setError('No secure login link found. Please request a new one.');
        return;
      }

      if (response.data.success && response.data.access_token) {
        localStorage.setItem('workerToken', response.data.access_token);
        localStorage.setItem('workerEmployee', JSON.stringify(response.data.employee));
        const slug = response.data.workforce_slug || workspaceSlugFromUrl;
        if (slug) {
          localStorage.setItem('workerWorkspaceSlug', slug);
        }
        if (response.data.next_path) {
          setNextWorkerPath(sanitizeWorkerNextPath(response.data.next_path));
        }
        if (shortCode && window?.history?.replaceState) {
          // Remove the bearer-like short code from the address bar immediately
          // after a successful exchange so subsequent same-origin requests do not
          // carry it in referrer URLs.
          window.history.replaceState({}, document.title, '/worker/verify');
        }

        setEmployee(response.data.employee || null);
        setIsApplicant(Boolean(response.data.is_applicant));
        setStatus('welcome');
        toast.success(`Welcome back, ${response.data.employee?.name || ''}!`.trim());

        // Kick off branding fetch without blocking the splash/redirect timing.
        loadBranding(slug);
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      setStatus('error');
      const message = err.response?.data?.detail || 'Login link is invalid or expired';
      setError(message);
      if (workspaceSlugFromUrl) {
        localStorage.setItem('workerWorkspaceSlug', workspaceSlugFromUrl);
      }
      toast.error(message);
    }
  }, [searchParams, shortCode, workspaceSlugFromUrl, loadBranding]);

  useEffect(() => {
    verifyAccess();
  }, [verifyAccess]);

  if (status === 'welcome') {
    const tenantName = branding?.tenant_name || branding?.brand_name || '';
    return (
      <TenantWelcomeSplash
        tenantName={tenantName}
        logoUrl={branding?.logo_url || ''}
        primaryColour={branding?.primary_colour || '#0F766E'}
        firstName={firstNameOf(employee?.name)}
        audienceLabel={isApplicant ? 'Applicant Portal' : 'Staff Portal'}
        message={isApplicant
          ? 'Your application is saved and ready to continue.'
          : 'Your work dashboard is ready.'}
        subMessage="Taking you to your next step…"
        continueLabel="Continue"
        autoAdvanceMs={1200}
        onContinue={goToNextAction}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
        {status === 'verifying' && (
          <>
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="h-10 w-10 text-blue-600 animate-spin" />
            </div>
            <h2 className="text-2xl font-semibold text-slate-800 mb-2">Verifying Your Login</h2>
            <p className="text-slate-600">Please wait while we log you in...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="h-10 w-10 text-red-600" />
            </div>
            <h2 className="text-2xl font-semibold text-slate-800 mb-2">Login Failed</h2>
            <p className="text-slate-600 mb-2">{error}</p>
            <p className="text-sm text-slate-500 mb-6">
              Secure links expire and can only be used once. Your progress is safe. Getting a new link
              will not restart your application.
            </p>

            <div className="space-y-3">
              <button
                onClick={() => {
                  const slug = knownWorkspaceSlug();
                  navigate(slug ? workforceReturnPath(slug) : '/access');
                }}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-teal-700 text-white rounded-lg hover:bg-teal-800 transition-colors font-medium"
                data-testid="worker-verify-resend"
              >
                <RefreshCw className="h-4 w-4" />
                Get a new secure link
              </button>
              <p className="text-xs text-slate-500 -mt-1">
                Return to your care provider&apos;s portal to enter your email and request a new link.
              </p>
              <button
                onClick={() => {
                  const slug = knownWorkspaceSlug();
                  navigate(slug ? workforceReturnPath(slug) : '/access');
                }}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium"
                data-testid="worker-verify-try-another-email"
              >
                <Mail className="h-4 w-4" />
                Try another email
              </button>
              <button
                onClick={() => navigate('/access')}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-2 text-sm text-slate-500 hover:text-slate-700 transition-colors"
                data-testid="worker-verify-portal-login"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back to Portal Login
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}